import classNames from "classnames";
import * as React from "react";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { AppButton } from "./Generic/AppButton";
import { If } from "./Generic/If";
import { Signature } from "./Signture";

export interface SignatureModalProps {
  onEnd: (dataURL: string | null) => void;
  initialData: string | null;
  disabled?: boolean;
  onClick?: () => boolean;
}

interface SignatureModalState {
  isModalOpen: boolean;
  isModalDismissing: boolean;
  imageData: string | null;
  dimensions: { width: number; height: number };
}

export class SignatureModal extends React.Component<SignatureModalProps, SignatureModalState> {
  modalElement: any;

  constructor(props: SignatureModalProps) {
    super(props);

    const rootElem = document.getElementById("root");

    if (rootElem === null) {
      throw new Error("Could not find root element");
    }

    this.state = {
      isModalOpen: false,
      isModalDismissing: false,
      imageData: this.props.initialData,
      dimensions: {
        width: rootElem.clientWidth,
        height: rootElem.clientHeight / 5,
      },
    };

    this.modalElement = null;
  }

  private signature: any = (React as any).createRef();
  private signatureRef: Signature | null = null;

  shouldComponentUpdate(nextProps: SignatureModalProps, nextState: SignatureModalState) {
    return (
      this.props.disabled !== nextProps.disabled ||
      this.props.initialData !== nextProps.initialData ||
      this.state.isModalOpen !== nextState.isModalOpen ||
      this.state.isModalDismissing !== nextState.isModalDismissing ||
      this.state.imageData !== nextProps.initialData
    );
  }

  componentDidUpdate(prevProps: SignatureModalProps) {
    if (this.props.initialData !== prevProps.initialData) {
      this.refreshImage(this.props.initialData);
      this.setState({ imageData: this.props.initialData });
    }
  }

  componentDidMount() {
    this.signature.getContext("2d").scale(2, 2);
    this.refreshImage(this.props.initialData);
  }

  refreshImage(data: string | null) {
    const ctx = this.signature.getContext("2d");
    if (data !== null) {
      const tempImage = new Image();

      tempImage.onload = () => {
        ctx.clearRect(0, 0, this.state.dimensions.width, this.state.dimensions.height);
        ctx.drawImage(tempImage, 0, 0);
      };

      tempImage.src = data;
    } else {
      ctx.clearRect(0, 0, this.state.dimensions.width, this.state.dimensions.height);
      ctx.font = "50px Roboto, Noto, sans-serif";
      ctx.fillStyle = "rgba(17, 17, 17, 0.5)";
      ctx.textAlign = "center";
      ctx.fillText(
        "Click to sign",
        this.state.dimensions.width / 2,
        this.state.dimensions.height / 2 + 10
      );
    }
  }

  onOk() {
    this.props.onEnd(this.state.imageData);
    this.refreshImage(this.state.imageData);
    this.setState({ isModalOpen: false });
  }

  onSignatureChange(dataUrl: string | null) {
    this.setState({ imageData: dataUrl });
  }

  handleClick() {
    if (this.props.disabled !== undefined && this.props.disabled) {
      return;
    }

    if (this.props.onClick !== undefined) {
      if (this.props.onClick()) {
        this.setState({ isModalOpen: true });
      }
    } else {
      this.setState({ isModalOpen: true });
    }
  }

  setModalRef = (element: any) => {
    if (this.modalElement) {
      this.modalElement.removeEventListener("touchmove", this.stopClickOuter);
    }
    if (element) {
      this.modalElement = element;
      this.modalElement.addEventListener("touchmove", this.stopClickOuter);
    }
  };

  stopClickOuter = (evt: any) => {
    if (evt.target === this.modalElement) {
      evt.stopPropagation();
      evt.preventDefault();
    }
  };

  render() {
    return (
      <div className="SignatureModal__container">
        <canvas
          onClick={() => this.handleClick()}
          className="SignatureModal__canvas"
          style={this.getCanvasStyle()}
          height={this.state.dimensions.height * 2}
          width={this.state.dimensions.width * 2}
          ref={(e: any) => (this.signature = e)}
        />
        <If condition={this.state.isModalOpen}>
          <div
            ref={this.setModalRef}
            className={classNames({
              SignatureModal__sig: true,
              SignatureModal__sigVisible: !this.state.isModalDismissing,
            })}
          >
            <div className="SignatureModal__dimmer" onClick={() => this.dismissModal()} />
            <div className="SignatureModal__sigContainer">
              <div className="SignatureModal__close" onClick={() => this.dismissModal()}>
                <img src="assets/times.svg" alt="close" />
              </div>
              <div className="SignatureModal__header">
                <h3>{translate(STRINGS.SUBMIT_YOUR_SIGNATURE)}</h3>
              </div>
              <Signature
                ref={(ref) => (this.signatureRef = ref)}
                onEnd={(dataUrl) => this.onSignatureChange(dataUrl)}
                initialData={this.state.imageData}
                width={this.state.dimensions.width}
                height={this.state.dimensions.height}
                className="SignatureModal__sigCanvas"
                buttonClassName="SignatureModal__sigClearButton"
                onClear={() => this.onSignatureChange(null)}
              />
              <div className="SignatureModal__buttonsContainer">
                <AppButton
                  color="primary"
                  variant="outline"
                  onClick={() => {
                    if (this.signatureRef !== null) {
                      this.signatureRef.onClickReset();
                    }
                  }}
                >
                  {translate(STRINGS.CLEAR)}
                </AppButton>

                <AppButton
                  color="primary"
                  variant="filled"
                  style={{ flexGrow: 1 }}
                  onClick={() => this.onOk()}
                >
                  {translate(STRINGS.OK)}
                </AppButton>
              </div>
            </div>
          </div>
        </If>
      </div>
    );
  }

  private dismissModal() {
    this.setState({ isModalDismissing: true });

    // let the close-animation happen
    setTimeout(() => this.setState({ isModalOpen: false, isModalDismissing: false }), 250);
  }

  private getCanvasStyle(): React.CSSProperties {
    return {
      pointerEvents: this.props.disabled !== undefined && this.props.disabled ? "none" : "all",
    };
  }
}
